import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield } from 'lucide-react';

const HeroSection = ({
  eyebrow,
  title,
  subtitle,
  primaryCTA,
  secondaryCTA,
  showTrustStrip = false,
  variant = 'default', // 'default' | 'minimal'
  children
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <section 
      className={`relative overflow-hidden ${
        variant === 'minimal' 
          ? 'py-16 md:py-24' 
          : 'py-20 md:py-32 lg:py-40'
      }`}
      data-testid="hero-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-warm-white via-warm-gray-50 to-warm-white" />
      <div 
        className="absolute inset-0 opacity-5" 
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23641404' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />

      <div className="container-custom relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-4xl mx-auto text-center"
        >
          {eyebrow && (
            <motion.div 
              variants={itemVariants}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-brand-maroon/10 text-brand-maroon font-medium text-sm mb-6"
              data-testid="hero-eyebrow"
            >
              <Shield className="w-4 h-4" />
              <span>{eyebrow}</span>
            </motion.div>
          )}

          <motion.h1 
            variants={itemVariants}
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground mb-6 leading-tight"
            data-testid="hero-title"
          >
            {title}
          </motion.h1>

          <motion.p 
            variants={itemVariants}
            className="text-lg md:text-xl text-muted-foreground mb-8 md:mb-10 leading-relaxed max-w-3xl mx-auto"
            data-testid="hero-subtitle"
          >
            {subtitle}
          </motion.p>

          {(primaryCTA || secondaryCTA) && (
            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              data-testid="hero-cta-group"
            >
              {primaryCTA && (
                <Button
                  asChild
                  size="lg"
                  className="bg-brand-maroon hover:bg-brand-maroon-dark text-white shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-primary-cta"
                >
                  <Link to={primaryCTA.href}>
                    {primaryCTA.text}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              )}
              {secondaryCTA && (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  data-testid="hero-secondary-cta"
                >
                  <Link to={secondaryCTA.href}>
                    {secondaryCTA.text}
                  </Link>
                </Button>
              )}
            </motion.div>
          )}

          {children && (
            <motion.div variants={itemVariants} className="mt-12">
              {children}
            </motion.div>
          )}
        </motion.div>

        {showTrustStrip && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-16 pt-12 border-t border-warm-gray-200"
            data-testid="trust-strip"
          >
            <p className="text-center text-sm text-muted-foreground mb-6">
              Trusted by leading organizations across the Middle East region
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8 opacity-60">
              {['ISO 27001', 'PCI DSS', 'SWIFT CSP', 'GDPR', 'Saudi PDPL'].map((cert) => (
                <div 
                  key={cert} 
                  className="px-6 py-3 bg-white rounded-lg shadow-sm font-medium text-sm"
                >
                  {cert}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;